import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DollarSign, Download, Check, Clock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface Payout {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  processed_at: string | null;
  payment_credential_id: string | null;
}

export const PayoutHistory = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPayouts();
  }, [user]);

  const loadPayouts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('payouts')
        .select('*')
        .eq('creator_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPayouts(data || []);
    } catch (error: any) {
      toast.error('Failed to load payout history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pending', icon: Clock, variant: 'secondary' as const },
      processing: { label: 'Processing', icon: Clock, variant: 'secondary' as const },
      completed: { label: 'Completed', icon: Check, variant: 'default' as const },
      failed: { label: 'Failed', icon: X, variant: 'destructive' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const exportPayouts = () => {
    const csv = [
      ['Date', 'Amount (Coins)', 'Amount (USD)', 'Status', 'Processed Date'].join(','),
      ...payouts.map(p => [
        new Date(p.created_at).toLocaleDateString(),
        p.amount,
        (p.amount * 0.01).toFixed(2),
        p.status,
        p.processed_at ? new Date(p.processed_at).toLocaleDateString() : 'N/A'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Payout history exported');
  };

  if (loading) {
    return (
      <Card className="glass-card">
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Loading payout history...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Payout History
          </CardTitle>
          {payouts.length > 0 && (
            <Button variant="outline" size="sm" onClick={exportPayouts}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {payouts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No payouts yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {payouts.map((payout) => (
              <div 
                key={payout.id}
                className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/5 transition-colors"
              >
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {payout.amount.toLocaleString()} coins
                    </span>
                    <span className="text-muted-foreground text-sm">
                      (≈ ${(payout.amount * 0.01).toFixed(2)})
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {new Date(payout.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                  {payout.processed_at && (
                    <div className="text-xs text-muted-foreground">
                      Processed: {new Date(payout.processed_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                {getStatusBadge(payout.status)}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
